// Google automatic sign-in (GIS auto_select) on top of Clerk — replaces Clerk's
// <GoogleOneTap />, which can't set auto_select. A returning user with one
// Google session that already consented is signed in without a click; anyone
// else gets the regular One Tap prompt. The Google client id is the one the
// Clerk instance's Google connection uses (custom credentials, Clerk dashboard).
// https://developers.google.com/identity/gsi/web/guides/automatic-sign-in-sign-out
//
// Sign-out: Google requires disableAutoSelect() after a sign-out, or the user is
// signed straight back in. Mounted inside <ClerkProvider> for the whole app so
// it sees the signed-in → signed-out transition; it prompts whenever signed out
// (the SPA shows the sign-in screen then).
import { useAuth, useClerk } from "@clerk/clerk-react";
import { useEffect } from "react";

type GoogleId = {
  initialize(o: Record<string, unknown>): void;
  prompt(): void;
  cancel(): void;
  disableAutoSelect(): void;
};
declare global {
  interface Window { google?: { accounts: { id: GoogleId } } }
}

const GSI_SRC = "https://accounts.google.com/gsi/client";
const SIGNED_IN_FLAG = "gsi:wasSignedIn";

let gsi: Promise<GoogleId> | null = null;
function loadGsi(): Promise<GoogleId> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  gsi ??= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.onload = () => (window.google?.accounts?.id ? resolve(window.google.accounts.id) : reject(new Error("gsi")));
    s.onerror = () => { gsi = null; reject(new Error("gsi")); };
    document.head.appendChild(s);
  });
  return gsi;
}

// the Clerk instance's Google client id (Environment.displayConfig) — read off
// the loaded clerk-js instance; the hook's wrapper may not proxy `environment`
type WithEnv = { environment?: { displayConfig?: { googleOneTapClientId?: string } } };
function googleClientId(clerk: unknown): string | undefined {
  const w = window as unknown as { Clerk?: WithEnv };
  return w.Clerk?.environment?.displayConfig?.googleOneTapClientId ?? (clerk as WithEnv).environment?.displayConfig?.googleOneTapClientId;
}

function flag(v?: boolean): boolean {
  try {
    if (v === true) localStorage.setItem(SIGNED_IN_FLAG, "1");
    else if (v === false) localStorage.removeItem(SIGNED_IN_FLAG);
    return localStorage.getItem(SIGNED_IN_FLAG) === "1";
  } catch {
    return false;
  }
}

export function GoogleAutoSignIn() {
  const clerk = useClerk();
  const { isLoaded, isSignedIn } = useAuth();

  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn) {
      flag(true);
      return;
    }
    const justSignedOut = flag();

    let cancelled = false;
    let id: GoogleId | null = null;
    loadGsi()
      .then((g) => {
        if (cancelled) return;
        id = g;
        if (justSignedOut) {
          g.disableAutoSelect(); // the next prompt needs a click (no sign-in loop)
          flag(false);
        }
        const clientId = googleClientId(clerk);
        if (!clientId) return;
        g.initialize({
          client_id: clientId,
          auto_select: true,
          cancel_on_tap_outside: false,
          itp_support: true,
          use_fedcm_for_prompt: true,
          context: "signin",
          callback: async ({ credential }: { credential: string }) => {
            try {
              const res = await clerk.authenticateWithGoogleOneTap({ token: credential });
              await clerk.handleGoogleOneTapCallback(res, {});
            } catch (e) {
              console.error("Google sign-in failed", e);
            }
          },
        });
        g.prompt();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      id?.cancel();
    };
  }, [clerk, isLoaded, isSignedIn]);

  return null;
}
